package co.maytes.standupbot.dashboard.pages.app.summary

import co.maytes.standupbot.dashboard.styles.Layout
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.ErrorState
import co.maytes.standupbot.dashboard.components.feedback.SkeletonRow
import co.maytes.standupbot.dashboard.components.layout.AdminGuard
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.AppPageHeader
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.StatCard
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertTriangle
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.features.analytics.DateRange
import co.maytes.standupbot.dashboard.features.analytics.DateRangePicker
import co.maytes.standupbot.dashboard.features.analytics.ResourceState
import co.maytes.standupbot.dashboard.features.analytics.viewPhase
import co.maytes.standupbot.dashboard.features.settings.RetentionCopy
import co.maytes.standupbot.dashboard.features.summary.SummaryCsvExport
import co.maytes.standupbot.dashboard.features.summary.SummaryFocus
import co.maytes.standupbot.dashboard.features.summary.rememberSummaryStore
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.summary.AccomplishmentSummaryDto
import co.maytes.standupbot.shared.dto.summary.DeliveredItemDto
import co.maytes.standupbot.shared.dto.summary.PersonAccomplishmentsDto
import co.maytes.standupbot.shared.dto.summary.TeamAccomplishmentsDto
import co.maytes.standupbot.shared.dto.summary.WorkItemDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Text

@Page("/app/summary")
@Composable
fun SummaryPage() {
    AuthGuard {
        AdminGuard {
            AppShell {
                SummaryContent()
            }
        }
    }
}

@Composable
private fun SummaryContent() {
    val auth = rememberAuthStore()
    val store = rememberSummaryStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id
    var range by remember { mutableStateOf(DateRange.thisWeek()) }

    LaunchedEffect(workspaceId, range.cacheKey) {
        if (!workspaceId.isNullOrBlank()) store.load(workspaceId, range)
    }

    val overview = (store.overviewState as? ResourceState.Loaded<AccomplishmentSummaryDto>)?.value

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("margin", "0 auto") }
            .maxWidth(Layout.wide),
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            ControlsRow(
                range = range,
                onRangeChange = { range = it },
                focus = store.focus,
                overview = overview,
                onFocusChange = { target -> scope.launch { store.setFocus(target) } },
                onExportCsv = {
                    val focused = (store.focusState as? ResourceState.Loaded<AccomplishmentSummaryDto>)?.value
                    val dto = if (store.focus is SummaryFocus.Overview) overview else focused ?: overview
                    if (dto != null) SummaryCsvExport.export(dto, range.label)
                },
            )

            val retentionNotice = RetentionCopy.summaryRangeNotice(
                from = range.from,
                today = Clock.System.todayIn(TimeZone.currentSystemDefault()),
                retentionDays = auth.currentUser?.workspace?.rawActivityRetentionDays ?: 30,
            )
            if (retentionNotice != null) {
                SpanText(
                    text = retentionNotice,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
            }

            if (store.focus is SummaryFocus.Overview) {
                OverviewSection(
                    state = store.overviewState,
                    range = range,
                    onFocusPerson = { id, name ->
                        scope.launch { store.setFocus(SummaryFocus.Person(id, name)) }
                    },
                )
            } else {
                FocusSection(
                    focus = store.focus,
                    state = store.focusState,
                    onBack = { scope.launch { store.setFocus(SummaryFocus.Overview) } },
                )
            }
        }
    }
}

@Composable
private fun ControlsRow(
    range: DateRange,
    onRangeChange: (DateRange) -> Unit,
    focus: SummaryFocus,
    overview: AccomplishmentSummaryDto?,
    onFocusChange: (SummaryFocus) -> Unit,
    onExportCsv: () -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
        AppPageHeader(
            eyebrow = "summary",
            title = "Accomplishment Summary",
            subtitle = "A verifiable ledger of delivered work. Pick a person or team to synthesize an AI report.",
        )
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth().gap(Spacing.md).styleModifier { property("flex-wrap", "wrap") },
        ) {
            DateRangePicker(value = range, onChange = onRangeChange)
            FocusPicker(focus = focus, overview = overview, onFocusChange = onFocusChange)
            Box(modifier = Modifier.styleModifier { property("margin-left", "auto") }) {
                Button(label = "Export CSV", variant = ButtonVariant.Ghost, onClick = onExportCsv)
            }
        }
    }
}

@Composable
private fun FocusPicker(
    focus: SummaryFocus,
    overview: AccomplishmentSummaryDto?,
    onFocusChange: (SummaryFocus) -> Unit,
) {
    val options = buildList {
        add(SelectOption(FOCUS_OVERVIEW, "Overview (everyone)"))
        overview?.persons?.forEach { add(SelectOption(focusKeyPerson(it.userId), it.displayName)) }
        overview?.teams?.forEach { add(SelectOption(focusKeyTeam(it.teamId), "Team · ${it.teamName}")) }
    }
    val selected = when (val f = focus) {
        SummaryFocus.Overview -> FOCUS_OVERVIEW
        is SummaryFocus.Person -> focusKeyPerson(f.userId)
        is SummaryFocus.Team -> focusKeyTeam(f.teamId)
    }
    Box(modifier = Modifier.styleModifier { property("min-width", "240px") }) {
        Select(
            value = selected,
            options = options,
            ariaLabel = "Focus on a person or team",
            onValueChange = { key ->
                onFocusChange(resolveFocus(key, overview))
            },
        )
    }
}

@Composable
private fun OverviewSection(
    state: ResourceState<AccomplishmentSummaryDto>,
    range: DateRange,
    onFocusPerson: (String, String) -> Unit,
) {
    StateSwap(state = state.viewPhase { it.persons.isEmpty() }) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            when (state) {
                ResourceState.Idle, ResourceState.Loading -> Column(
                    modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                ) {
                    repeat(4) { SkeletonRow() }
                }
                is ResourceState.Error -> ErrorState(message = state.error.displayMessage)
                is ResourceState.Loaded -> {
                    val dto = state.value
                    if (dto.persons.isEmpty()) {
                        EmptyState(
                            title = "No standups in range",
                            subtitle = "No posted standup sections found between ${range.from} and ${range.to}.",
                        )
                    } else {
                        KpiStrip(dto)
                        OverviewTable(dto, onFocusPerson)
                    }
                }
            }
        }
    }
}

@Composable
private fun FocusSection(
    focus: SummaryFocus,
    state: ResourceState<AccomplishmentSummaryDto>,
    onBack: () -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth().gap(Spacing.sm).styleModifier { property("flex-wrap", "wrap") },
        ) {
            Button(label = "← Back to overview", variant = ButtonVariant.Secondary, onClick = onBack)
            FocusBadge(focus, state)
        }
        StateSwap(state = state.viewPhase()) {
            when (state) {
                ResourceState.Idle, ResourceState.Loading -> Column(
                    modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                ) {
                    SpanText(
                        text = "Synthesizing report…",
                        modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary),
                    )
                    repeat(4) { SkeletonRow() }
                }
                is ResourceState.Error -> ErrorState(message = state.error.displayMessage)
                is ResourceState.Loaded -> FocusReport(focus, state.value)
            }
        }
    }
}

@Composable
private fun FocusBadge(focus: SummaryFocus, state: ResourceState<AccomplishmentSummaryDto>) {
    val dto = (state as? ResourceState.Loaded<AccomplishmentSummaryDto>)?.value
    when {
        dto == null -> Unit
        dto.synthesized -> Badge(label = "AI report", tone = BadgeTone.Accent)
        else -> Badge(label = "Facts only", tone = BadgeTone.Neutral)
    }
    if (dto != null && dto.model.isNotBlank()) {
        Badge(label = dto.model, tone = BadgeTone.Neutral)
    }
}

@Composable
private fun FocusReport(focus: SummaryFocus, dto: AccomplishmentSummaryDto) {
    when (focus) {
        is SummaryFocus.Person -> {
            val person = dto.persons.firstOrNull { it.userId == focus.userId }
            if (person == null) {
                EmptyState(title = focus.displayName, subtitle = "No activity for this person in range.")
            } else {
                Card { PersonCardBody(person) }
            }
        }
        is SummaryFocus.Team -> {
            val team = dto.teams.firstOrNull { it.teamId == focus.teamId }
            if (team == null) {
                EmptyState(title = focus.teamName, subtitle = "No activity for this team in range.")
            } else {
                TeamCard(team)
            }
        }
        SummaryFocus.Overview -> Unit
    }
}

@Composable
private fun OverviewTable(dto: AccomplishmentSummaryDto, onFocusPerson: (String, String) -> Unit) {
    Card {
        Column(modifier = Modifier.fillMaxWidth()) {
            OverviewHeaderRow()
            dto.persons.forEach { person ->
                OverviewPersonRow(person, onFocusPerson)
            }
        }
    }
}

@Composable
private fun OverviewHeaderRow() {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.sm)
            .styleModifier {
                property("padding", "8px 0")
                property("border-bottom", "1px solid ${Palette.border}")
            },
    ) {
        OverviewCell("Person", grow = true, header = true)
        OverviewCell("Issues", header = true)
        OverviewCell("PRs", header = true)
        OverviewCell("Blockers", header = true)
        OverviewCell("Active", header = true)
        OverviewCell("Standups", header = true)
        Box(modifier = Modifier.styleModifier { property("width", "120px") })
    }
}

@Composable
private fun OverviewPersonRow(person: PersonAccomplishmentsDto, onFocusPerson: (String, String) -> Unit) {
    val m = person.metrics
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.sm)
            .styleModifier {
                property("padding", "10px 0")
                property("border-bottom", "1px solid ${Palette.border}")
            },
    ) {
        OverviewCell(person.displayName, grow = true, strong = true)
        OverviewCell(m.issuesCompleted.toString())
        OverviewCell(m.prsMerged.toString())
        OverviewCell(m.blockerCount.toString())
        OverviewCell(m.activeDays.toString())
        OverviewCell(m.standupsPosted.toString())
        Box(modifier = Modifier.styleModifier { property("width", "120px") }) {
            Button(
                label = "View report",
                variant = ButtonVariant.Secondary,
                onClick = { onFocusPerson(person.userId, person.displayName) },
            )
        }
    }
}

@Composable
private fun OverviewCell(text: String, grow: Boolean = false, header: Boolean = false, strong: Boolean = false) {
    Box(
        modifier = Modifier.styleModifier {
            if (grow) {
                property("flex", "1 1 auto")
                property("min-width", "140px")
            } else {
                property("width", "72px")
                property("text-align", "right")
            }
        },
    ) {
        SpanText(
            text = text,
            modifier = Modifier
                .fontSize(if (header) Typography.caption.size else Typography.bodySmall.size)
                .fontWeight(if (header || strong) 600 else 400)
                .color(if (header) Palette.textTertiary else Palette.textPrimary)
                .styleModifier {
                    if (header) {
                        property("text-transform", "none")
                        property("letter-spacing", "0.04em")
                    }
                },
        )
    }
}

@Composable
private fun KpiStrip(dto: AccomplishmentSummaryDto) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .gap(Spacing.md)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
        ) {
            KpiCard("People active", dto.totalPeopleActive.toString())
            KpiCard("Standups posted", dto.totalStandupsPosted.toString())
            KpiCard("Issues completed", dto.totalIssuesCompleted.toString())
            KpiCard("PRs merged", dto.totalPrsMerged.toString())
            KpiCard("Open blockers", dto.totalOpenBlockers.toString())
        }
    }
}

@Composable
private fun KpiCard(label: String, value: String) {
    Box(modifier = Modifier.styleModifier {
        property("flex", "1 1 200px")
        property("display", "flex")
    }) {
        StatCard(label = label, value = value)
    }
}

@Composable
private fun PersonCardBody(person: PersonAccomplishmentsDto) {
    SpanText(
        text = person.displayName,
        modifier = Modifier
            .fontSize(Typography.h2.size)
            .fontWeight(600)
            .color(Palette.textPrimary),
    )
    if (person.headline.isNotBlank()) {
        SpanText(
            text = person.headline,
            modifier = Modifier
                .margin(top = Spacing.xs)
                .fontSize(Typography.body.size)
                .lineHeight(Typography.body.lineHeight)
                .color(Palette.textSecondary),
        )
    }

    MetricChips(person)

    if (person.openBlockers.isNotEmpty()) {
        Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md).gap(Spacing.xs)) {
            SectionLabel("Blocked")
            Row(modifier = Modifier.fillMaxWidth().gap(Spacing.xs).styleModifier { property("flex-wrap", "wrap") }) {
                person.openBlockers.forEach { BlockerChip(it) }
            }
        }
    }

    if (person.delivered.isNotEmpty()) {
        Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md).gap(Spacing.sm)) {
            SectionLabel("Delivered")
            DeliveredByTheme(person.delivered, person.themes)
        }
    }

    if (person.inProgress.isNotEmpty()) {
        Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md).gap(Spacing.xs)) {
            SectionLabel("In progress")
            person.inProgress.forEach { WorkRow(it) }
        }
    }

    if (person.days.isNotEmpty()) {
        DailyDetailDisclosure(person)
    }
}

@Composable
private fun MetricChips(person: PersonAccomplishmentsDto) {
    val m = person.metrics
    val parts = buildList {
        add("${m.standupsPosted} standup${plural(m.standupsPosted)}")
        add("${m.activeDays} active day${plural(m.activeDays)}")
        if (m.prsMerged > 0) add("${m.prsMerged} merged")
        if (m.reviewsGiven > 0) add("${m.reviewsGiven} review${plural(m.reviewsGiven)}")
        if (m.commitCount > 0) add("${m.commitCount} commit${plural(m.commitCount)}")
    }
    if (parts.isEmpty()) return
    Box(modifier = Modifier.margin(top = Spacing.sm)) {
        Badge(label = parts.joinToString(" · "), tone = BadgeTone.Neutral)
    }
}

@Composable
private fun DeliveredByTheme(delivered: List<DeliveredItemDto>, themes: List<String>) {
    val grouped = delivered.groupBy { themeOf(it.ref) }
    val order = themes.filter { grouped.containsKey(it) } + grouped.keys.filter { it !in themes }.sorted()
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
        order.forEach { theme ->
            val items = grouped[theme] ?: return@forEach
            Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
                SpanText(
                    text = theme,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .fontWeight(600)
                        .color(Palette.textTertiary)
                        .styleModifier { property("text-transform", "none"); property("letter-spacing", "0.04em") },
                )
                items.forEach { DeliveredRow(it) }
            }
        }
    }
}

@Composable
private fun DeliveredRow(item: DeliveredItemDto) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth().gap(Spacing.xs).styleModifier { property("flex-wrap", "wrap") },
    ) {
        Box(modifier = Modifier.color(Palette.success).styleModifier { property("display", "inline-flex") }) {
            LucideCheck(size = 15)
        }
        RefChip(ref = item.ref, url = item.url)
        SpanText(
            text = item.title,
            modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textPrimary),
        )
    }
}

@Composable
private fun WorkRow(item: WorkItemDto) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth().gap(Spacing.xs).styleModifier { property("flex-wrap", "wrap") },
    ) {
        RefChip(ref = item.ref, url = item.url)
        SpanText(
            text = item.title,
            modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
        )
    }
}

@Composable
private fun BlockerChip(item: WorkItemDto) {
    Box(modifier = Modifier.styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "6px")
        property("padding", "3px 9px")
        property("border-radius", "${Radius.full}")
        property("background-color", "rgba(220, 38, 38, 0.12)")
        property("color", "#B91C1C")
        property("box-shadow", "inset 0 0 0 1px rgba(220, 38, 38, 0.26)")
        property("white-space", "nowrap")
    }) {
        Box(modifier = Modifier.styleModifier { property("display", "inline-flex") }) {
            LucideAlertTriangle(size = 13)
        }
        SpanText(
            text = item.ref + (if (item.title.isNotBlank()) " · ${item.title}" else ""),
            modifier = Modifier.fontSize(Typography.caption.size).fontWeight(550),
        )
    }
}

@Composable
private fun RefChip(ref: String, url: String?) {
    val chipMod = Modifier.styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("padding", "1px 7px")
        property("border-radius", "${Radius.sm}")
        property("background-color", Palette.surfaceSubtle.toString())
        property("box-shadow", "inset 0 0 0 1px ${Palette.border}")
        property("font-family", Typography.FONT_MONO)
        property("font-size", "12px")
        property("font-weight", "600")
        property("white-space", "nowrap")
    }
    if (url.isNullOrBlank()) {
        Box(modifier = chipMod.color(Palette.textSecondary)) {
            SpanText(text = ref)
        }
    } else {
        A(
            href = url,
            attrs = chipMod
                .color(Palette.accent)
                .styleModifier { property("text-decoration", "none") }
                .onClick { it.stopPropagation() }
                .toAttrs {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                },
        ) { Text(ref) }
    }
}

@Composable
private fun DailyDetailDisclosure(person: PersonAccomplishmentsDto) {
    var open by remember { mutableStateOf(false) }
    Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md).gap(Spacing.xs)) {
        Box(
            modifier = Modifier
                .color(Palette.accent)
                .fontSize(Typography.bodySmall.size)
                .fontWeight(600)
                .styleModifier { property("cursor", "pointer"); property("user-select", "none") }
                .onClick { open = !open },
        ) {
            SpanText(text = if (open) "Hide daily detail" else "Show daily detail (${person.days.size})")
        }
        if (open) {
            Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
                person.days.forEach { day ->
                    Column(modifier = Modifier.fillMaxWidth()) {
                        SpanText(
                            text = day.date + (day.standupChannelName?.let { " · #$it" } ?: ""),
                            modifier = Modifier
                                .fontSize(Typography.bodySmall.size)
                                .fontWeight(600)
                                .color(Palette.textTertiary)
                                .margin(bottom = Spacing.xs),
                        )
                        if (day.yesterday.isNotBlank()) LabeledField("Yesterday", day.yesterday)
                        if (day.today.isNotBlank()) LabeledField("Today", day.today)
                        if (day.blockers.isNotBlank()) LabeledField("Blockers", day.blockers)
                    }
                }
            }
        }
    }
}

@Composable
private fun TeamCard(team: TeamAccomplishmentsDto) {
    Card {
        SpanText(
            text = team.teamName,
            modifier = Modifier
                .fontSize(Typography.h2.size)
                .fontWeight(600)
                .color(Palette.textPrimary),
        )
        TeamThroughputLine(team)
        if (team.headline.isNotBlank()) {
            SpanText(
                text = team.headline,
                modifier = Modifier
                    .margin(top = Spacing.xs)
                    .fontSize(Typography.body.size)
                    .lineHeight(Typography.body.lineHeight)
                    .color(Palette.textSecondary),
            )
        }
        if (team.delivered.isNotEmpty()) {
            Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md).gap(Spacing.sm)) {
                SectionLabel("Team delivered")
                DeliveredByTheme(team.delivered, team.themes)
            }
        }
        if (team.persons.isNotEmpty()) {
            Column(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg).gap(Spacing.sm)) {
                SectionLabel("Members")
                team.persons.forEach { person ->
                    Card(modifier = Modifier.fillMaxWidth()) {
                        PersonCardBody(person)
                    }
                }
            }
        }
    }
}

@Composable
private fun TeamThroughputLine(team: TeamAccomplishmentsDto) {
    val t = team.throughput
    val parts = buildList {
        add("${t.members} member${plural(t.members)}")
        add("${t.standupsPosted} standup${plural(t.standupsPosted)}")
        add("${t.issuesCompleted} issue${plural(t.issuesCompleted)}")
        add("${t.prsMerged} merged")
        if (t.openBlockers > 0) add("${t.openBlockers} blocker${plural(t.openBlockers)}")
    }
    Box(modifier = Modifier.margin(top = Spacing.xs)) {
        Badge(
            label = parts.joinToString(" · "),
            tone = if (t.openBlockers > 0) BadgeTone.Warning else BadgeTone.Neutral,
        )
    }
}

@Composable
private fun SectionLabel(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .fontWeight(600)
            .color(Palette.textSecondary),
    )
}

@Composable
private fun LabeledField(label: String, value: String) {
    Row(modifier = Modifier.fillMaxWidth().gap(Spacing.xs).margin(bottom = Spacing.xxs)) {
        SpanText(
            text = "$label:",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(600)
                .color(Palette.textSecondary)
                .styleModifier { property("min-width", "80px") },
        )
        SpanText(
            text = value,
            modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textPrimary),
        )
    }
}

private fun themeOf(ref: String): String {
    if (ref.lastIndexOf('/') >= 0) {
        val repoPart = ref.substringBefore('#').substringAfterLast('/')
        if (repoPart.isNotBlank()) return repoPart
    }
    val hash = ref.indexOf('#')
    if (hash > 0) return ref.substring(0, hash)
    val dash = ref.indexOf('-')
    if (dash > 0) return ref.substring(0, dash)
    return ref
}

private fun plural(n: Int): String = if (n == 1) "" else "s"

private const val FOCUS_OVERVIEW = "overview"

private fun focusKeyPerson(userId: String): String = "person:$userId"

private fun focusKeyTeam(teamId: String): String = "team:$teamId"

private fun resolveFocus(key: String, overview: AccomplishmentSummaryDto?): SummaryFocus = when {
    key.startsWith("person:") -> {
        val id = key.removePrefix("person:")
        val name = overview?.persons?.firstOrNull { it.userId == id }?.displayName ?: id
        SummaryFocus.Person(id, name)
    }
    key.startsWith("team:") -> {
        val id = key.removePrefix("team:")
        val name = overview?.teams?.firstOrNull { it.teamId == id }?.teamName ?: id
        SummaryFocus.Team(id, name)
    }
    else -> SummaryFocus.Overview
}
